/**
 * GTM data layer — the translation between our PostHog event vocabulary and the
 * event names the analytics team's tracking spec asks for.
 *
 * WHY A TRANSLATION rather than renaming our events: the PostHog names are
 * referenced by existing dashboards and funnels (see the wardrobe-build funnel
 * comments in analytics.ts, which explicitly ask that the names stay stable).
 * Renaming them to match the spec would break historical continuity on our side
 * for no gain on theirs. So `trackEvent` keeps emitting what it always has, and
 * anything with a spec equivalent is ALSO pushed to the data layer under the
 * spec's name and parameter spelling.
 *
 * Events with no entry in SPEC_EVENTS are PostHog-only and never reach GTM.
 * That is deliberate — the spec is a short, curated list, and forwarding all
 * ~60 of our events would bury it.
 */

/**
 * The CTA vocabulary.
 *
 * `cta_name` and `cta_location` are free text as far as GA4 is concerned, which
 * is how you end up with forty spellings of the same button and data nobody can
 * group. Typing them means a new CTA is a deliberate addition here rather than
 * an ad-hoc string at a call site.
 *
 * `cta_name` is the INTENT, not the label — the same destination keeps one name
 * even where the wording differs ("Explore" in the hero and "Try Demo" in the
 * footer are both `try_demo`).
 */
export type CtaName =
  | "get_started"
  | "try_demo"
  | "login"
  | "contact_support"
  | "view_pricing";

/** Where on the page the CTA sits, top to bottom. */
export type CtaLocation =
  | "header"
  | "hero"
  | "closing_cta"
  | "footer"
  | "blog_post";

type DataLayerRecord = Record<string, unknown> & { event: string };

declare global {
  interface Window {
    /**
     * Holds event records AND the raw `arguments` objects that Consent Mode
     * commands must be pushed as (see shared/lib/consent.ts) — hence the loose
     * element type rather than DataLayerRecord[].
     */
    dataLayer?: unknown[];
  }
}

type Props = Record<string, unknown> | undefined;

/**
 * Push a raw event to the GTM data layer.
 *
 * Creates the array if GTM hasn't loaded yet. The data layer is a plain array
 * that the container drains on arrival, so events pushed before GTM is ready
 * are replayed rather than dropped — the same queue-then-flush guarantee
 * `withPostHog` gives us on the PostHog side.
 */
export function pushToDataLayer(event: string, params?: Props): void {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...omitUndefined(params) });
}

/**
 * GTM reports an explicitly-undefined parameter as the string "undefined" in
 * GA4 rather than omitting it, which turns an absent value into a real one.
 */
function omitUndefined(params: Props): Record<string, unknown> {
  if (!params) return {};
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );
}

type SpecEvent = {
  /** The event name as the spec spells it. */
  event: string;
  /** Translates our PostHog properties into the spec's parameter names. */
  params?: (props: Record<string, unknown>) => Record<string, unknown>;
};

/**
 * Our event name → the spec's event.
 *
 * Only events whose mapping is settled appear here. Deliberately absent:
 *   - `first_wardrobe_item_uploaded` / `first_outfit_generated` — the browser
 *     cannot know whether something is a user's first; these need a server
 *     signal before they can be fired at all.
 *   - `article_read`, `internal_link_click` — blog-side, tracked separately.
 *   - `share_click`, `newsletter_signup` — no such feature exists yet.
 */
/**
 * Our onboarding step names → the tracking spec's. Anything unmapped (a step
 * added later, or the archived steps 4–7) passes through as-is rather than
 * being dropped, so a new step shows up in GA4 under its own name instead of
 * vanishing.
 */
const ONBOARDING_STEP_NAMES: Record<string, string> = {
  personal_details: "basic_info", // step 1 — details + email verification
  skin_analysis: "skin_analysis", // step 2
  body_type: "body_profile", // step 3
};

function translateStepName(name: unknown): unknown {
  if (typeof name !== "string") return name;
  return ONBOARDING_STEP_NAMES[name] ?? name;
}

const SPEC_EVENTS: Record<string, SpecEvent> = {
  // --- Already identical in name and parameters ---
  sign_up: { event: "sign_up", params: (p) => ({ method: p.method }) },
  onboarding_complete: { event: "onboarding_complete" },

  // --- New events, defined spec-side only ---
  signup_start: {
    event: "signup_start",
    params: (p) => ({ signup_method: p.signup_method }),
  },
  cta_click: {
    event: "cta_click",
    params: (p) => ({
      cta_name: p.cta_name,
      cta_location: p.cta_location,
      destination_url: p.destination_url,
    }),
  },

  // --- Renames ---

  /**
   * Note this maps from `wardrobe_build_succeeded`, NOT `dress_uploaded`.
   * `dress_uploaded` is dead code — defined in analytics.ts but never called.
   * The terminal success event every upload path actually fires is
   * `wardrobe_build_succeeded` (AddDressModal wraps onSuccess to guarantee it).
   */
  wardrobe_build_succeeded: {
    event: "wardrobe_item_uploaded",
    params: (p) => ({ upload_method: p.upload_method }),
  },

  shopping_generated: {
    event: "shopping_analysis_complete",
    params: (p) => ({ analysis_type: p.shopping_type }),
  },

  /**
   * `step_number` is the spec's spelling of what we call `step`, and the step
   * NAMES are translated to the spec's vocabulary by ONBOARDING_STEP_NAMES.
   *
   * The spec listed these in the wrong order (it had body_profile at step 2 and
   * skin_analysis at step 3; the app does skin analysis second). The app is the
   * fact, so the order below follows the app and only the wording is the
   * spec's. PostHog keeps emitting the original names so its history stays
   * continuous — this rename exists only on the GTM side.
   */
  onboarding_step: {
    event: "onboarding_step_complete",
    params: (p) => ({
      step_number: p.step,
      step_name: translateStepName(p.step_name),
    }),
  },

  /**
   * Both outfit-producing features map onto the spec's single
   * `outfit_generated`, distinguished by `feature_name`. That is the reading
   * that makes the parameter meaningful — if the event only ever covered Look
   * Lab there would be nothing for `feature_name` to vary over.
   */
  outfit_generated: {
    event: "outfit_generated",
    params: (p) => ({ feature_name: p.feature_name ?? "look_lab" }),
  },
  daily_drip_generated: {
    event: "outfit_generated",
    params: () => ({ feature_name: "daily_drip" }),
  },

  /**
   * GA4's standard ecommerce shape. `transaction_id` is what GA4 uses to
   * recognise a purchase it has already counted — without it, a refresh of the
   * confirmation page is counted as a second sale.
   */
  purchase_completed: {
    event: "purchase",
    params: (p) => ({
      transaction_id: p.transaction_id,
      value: p.price,
      currency: p.currency,
    }),
  },
};

/**
 * Forward one PostHog event to GTM if the spec has an equivalent. Called from
 * `trackEvent`, so every mapped event reaches both sinks from a single call
 * site and the two can't drift apart.
 */
export function forwardToDataLayer(event: string, properties?: Props): void {
  const spec = SPEC_EVENTS[event];
  if (!spec) return;

  pushToDataLayer(spec.event, spec.params?.(properties ?? {}));
}

/** Exported for tests — the set of our event names that reach GTM. */
export const MAPPED_EVENTS = Object.keys(SPEC_EVENTS);
